import * as THREE from 'three';
import { DEFAULT_DENSITY_ALPHA } from './PointCloudMaterial';

/**
 * Fullscreen composite for the high-precision X-ray (density mode) path.
 *
 * The point pass accumulates a per-pixel POINT COUNT into a float render
 * target (additive blending, each point adds 1 to R). This pass maps that
 * count to brightness with `1 - (1 - alpha)^count`, which is exactly what
 * the legacy 8-bit "over" blend converges to, but without the 1/255
 * quantization floor: alpha can go arbitrarily small, so arbitrarily many
 * points are needed before a pixel saturates to white.
 */
export class DensityCompositeMaterial extends THREE.RawShaderMaterial {
  constructor() {
    super({
      uniforms: {
        uDensity: { value: null as THREE.Texture | null },
        // log(1 - alpha), precomputed on the CPU: brightness = 1 - exp(count * uLogOneMinusAlpha)
        uLogOneMinusAlpha: { value: Math.log(1 - DEFAULT_DENSITY_ALPHA) },
      },
      vertexShader: `
        precision highp float;
        in vec3 position;
        in vec2 uv;
        out vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform sampler2D uDensity;
        uniform float uLogOneMinusAlpha;
        in vec2 vUv;
        out vec4 fragColor;
        void main() {
          float count = texture(uDensity, vUv).r;
          float brightness = 1.0 - exp(count * uLogOneMinusAlpha);
          fragColor = vec4(vec3(brightness), 1.0);
        }
      `,
      glslVersion: THREE.GLSL3,
      // Add onto whatever is already on screen (the view's background) — the
      // legacy path also only ever brightens the framebuffer.
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
  }

  setDensityTexture(texture: THREE.Texture): void {
    this.uniforms.uDensity.value = texture;
  }

  /** Per-point alpha; must be in (0, 1). */
  setAlpha(alpha: number): void {
    const clamped = Math.min(Math.max(alpha, 1e-7), 0.999999);
    this.uniforms.uLogOneMinusAlpha.value = Math.log(1 - clamped);
  }
}
